import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import Field from '../../../../../../components/Form/Field'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { transformGendersToBE } from '../../../../lineItemHelpers'

import {
  clearUpdateDv360BulkTargeting,
  updateDv360BulkTargeting
} from '../../../../../../modules/actions/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import {
  dv360BulkTargetingWasUpdatedSelector,
  dv360TargetingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../modules/selectors/dv360Constants'

import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../constants/changeHistory'
import { GENDER_OPTION } from '../../../../../ReusableFormFields/GenderFields/fields'
import { targetingTypes } from '../../../LineItemDv360Create/fields'
import { getInitialValues } from './fields'
import { genderOptions } from '../../../../fields'

const GenderSectionForm = ({ editItemData: item, title, ...formProps }) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)
  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)

  const initialValues = useMemo(() => {
    return getInitialValues(targeting)
  }, [targeting])

  const handleSubmit = useCallback(
    values => {
      const genders = transformGendersToBE(values)

      const requests = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_GENDER,
          assigned_targeting_options: genders.map(gender => ({
            gender
          }))
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <Field
        formik={purifiedFormik}
        name={GENDER_OPTION}
        placeholder={t('Gender')}
        options={genderOptions}
        isSearchable={false}
      />
    </EditForm>
  )
}

export default GenderSectionForm
